import React from 'react';
import PropTypes from 'prop-types';
import { SortButton } from './SortButton';
import ToolLabel from '../../../../ToolLabel/ToolLabel';
import MLTooltip from '../../../../MLTooltip/MLTooltip';
import ManualAssignmentActions from '../../../Components/ManualAssigmentActions';
import {
  isAssignmentInProgress,
  getToolIds,
  shouldHideSidePanel,
} from '@macmillan-learning/gradebook-shared';
import { getConfigObject } from '../../../../../utils/config';
import _filter from 'lodash/filter';
import _uniq from 'lodash/uniq';
import _flattenDeep from 'lodash/flattenDeep';
import _sumBy from 'lodash/sumBy';
import _isEmpty from 'lodash/isEmpty';
// Import styles from V1 AssignmentHeaderLabelAndTool for consistent ToolLabel styling
import toolLabelStyles from '../../../../AssignmentHeaderLabelAndTool/AssignmentHeaderLabelAndTool.module.css';
import styles from './AssignmentHeaderCell.module.css';

/**
 * Assignment header cell component.
 *
 * Accessibility approach:
 * - The assignment name/title is the interactive element (not the entire cell)
 * - This avoids nested interactive elements (cell + sort button)
 * - For manual assignments, ManualAssignmentActions handles its own interaction
 * - For other assignments, clicking the name opens the side panel
 */
export function AssignmentHeaderCell({
  name,
  dueDate,
  formatDate,
  columnId,
  sortState = false,
  onSort,
  assignment,
  disableManualAssignmentActions = false,
  onAssignmentClick,
}) {
  // Determine if side panel should be active for this tool
  const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();
  const shouldSidePanelBeActive =
    FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(assignment?.tool);

  // "In Progress" label (for non-manual assignments before due date)
  const isManualAssignment = assignment?.tool === getToolIds().manual;

  // Non-linkable tools - matches V1 logic (reef/iClicker are not clickable)
  const { reef, manual } = getToolIds();
  const isNonLinkableTool = { [reef]: true, [manual]: true };
  const isToolNonLinkable =
    !isManualAssignment &&
    (_isEmpty(assignment?.tool) || isNonLinkableTool[assignment?.tool]);

  const canOpenSidePanel =
    shouldSidePanelBeActive && onAssignmentClick && !isToolNonLinkable;

  const inProgressLabel = !isManualAssignment &&
    assignment &&
    isAssignmentInProgress(assignment) && (
      <div className={toolLabelStyles.inProgress}>(In Progress)</div>
    );

  // Exception count - filter to due_date exceptions only
  const exceptions =
    _filter(assignment?.Exceptions || [], { exception_type: 'due_date' }) || [];
  const usersExceptions = _uniq(
    _flattenDeep(exceptions.map((ex) => ex.user_ids)),
  );
  const numExceptions = _sumBy(usersExceptions, (userId) =>
    userId !== null ? 1 : 0,
  );

  // Handle assignment name click - opens side panel
  const handleNameClick = () => {
    if (canOpenSidePanel) {
      onAssignmentClick(assignment);
    }
  };

  // Handle keyboard activation for the assignment name
  const handleNameKeyDown = (e) => {
    if (canOpenSidePanel && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onAssignmentClick(assignment);
    }
  };

  return (
    <div className={styles.container}>
      {/* Content area - matches V1 .assignmentHeaderContent */}
      <div className={styles.headerContent}>
        {/* Assignment name - for manual assignments, use ManualAssignmentActions with showAsLabel */}
        {isManualAssignment ? (
          // Stop propagation to prevent cell click from interfering with dropdown
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            className={styles.assignmentNameContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <ManualAssignmentActions
              showAsLabel
              assignment={assignment}
              tooltipContent={name}
              disableActions={disableManualAssignmentActions}
            />
          </div>
        ) : (
          <MLTooltip content={name} placement="top">
            <div className={styles.assignmentNameContainer}>
              <span
                className={`${styles.assignmentName} ${
                  canOpenSidePanel ? styles.clickable : styles.nonClickable
                }`}
                onClick={canOpenSidePanel ? handleNameClick : undefined}
                onKeyDown={canOpenSidePanel ? handleNameKeyDown : undefined}
                role={canOpenSidePanel ? 'button' : undefined}
                tabIndex={canOpenSidePanel ? 0 : -1}
              >
                {name}
              </span>
            </div>
          </MLTooltip>
        )}

        {/* Tool label with in-progress indicator */}
        {assignment?.tool && (
          <ToolLabel
            activity={assignment}
            classNames={toolLabelStyles.toolLabel}
            containerClass={toolLabelStyles.toolLabelOverride}
            isAdminOrInstructor={true}
            inProgressLabel={inProgressLabel}
            isIndividualStudentGradebook={false}
          />
        )}

        {/* Due date */}
        <div
          className={`${toolLabelStyles.dateStyle} ${styles.dueDateContainer}`}
        >
          Due Date:{' '}
          <span className={toolLabelStyles.dateContent}>
            {formatDate(dueDate)}
          </span>
          {/* Exception count - displayed below due date without parentheses (matches V1) */}
          {numExceptions > 0 && (
            <div className={styles.exceptionLabel}>
              {numExceptions} exception{numExceptions > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Sort button - positioned at bottom-right (matches V1 Sorter behavior) */}
      {columnId && onSort && (
        <div className={styles.sortButtonContainer}>
          <SortButton
            columnId={columnId}
            sortState={sortState}
            onSort={onSort}
            assignmentName={name}
          />
        </div>
      )}
    </div>
  );
}

AssignmentHeaderCell.propTypes = {
  name: PropTypes.string.isRequired,
  dueDate: PropTypes.string,
  formatDate: PropTypes.func.isRequired,
  columnId: PropTypes.string,
  sortState: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onSort: PropTypes.func,
  assignment: PropTypes.object,
  disableManualAssignmentActions: PropTypes.bool,
  onAssignmentClick: PropTypes.func,
};

AssignmentHeaderCell.defaultProps = {
  dueDate: null,
  columnId: null,
  sortState: false,
  onSort: null,
  assignment: null,
  disableManualAssignmentActions: false,
  onAssignmentClick: null,
};
